import {
  ClearableBy,
  ErrorCode,
  Severity,
  type Fault,
} from '@sb-protogen/external/external_control';
import type * as zod from 'zod';

import * as log from '@sb/log';
import {
  ControlType,
  StreamType,
} from '@sb/messenger/auto_generated/messenger/external_control_bot_service';
import { isOnlinePlannerAllowedForThisModel } from '@sb/robot';
import { FailureKind } from '@sb/routine-runner/FailureKind';
import { getFeatureFlagSync } from '@sb/service-interfaces/host/featureFlags';

import type { StepAction, StepPlayArguments } from '../Step';
import Step from '../Step';

import Arguments, {
  describeTcStreamingFallback,
  resolveServoConfig,
  resolveTcServoModality,
  type TcServoModality,
  type TcStreamingFlags,
} from './Arguments';
import { TcServoSession } from './TcServoSession';
import Variables from './Variables';

/**
 * Render a fault for the routine's failure message.
 *
 * The message is what a person reading the routine's failure sees, so the code,
 * severity and who may clear it all belong in it. The customer-facing sentence
 * comes from the fault itself; nothing is re-worded here.
 *
 * Deliberately NOT routed through `operatorFaultMessage`. The reader of this
 * surface is an integrator debugging their own stream, not an operator at the
 * robot: it is also what `libs/sb-robot-api` hands back as the recovery
 * `reason`, and the same client receives the same incident as a `Fault` on the
 * wire. Collapsing the streaming codes to one generic sentence here would drop
 * exactly the detail that reader needs, and would make the three descriptions
 * of one incident disagree.
 */
function describeFault(fault: Fault): string {
  const code = ErrorCode[fault.code] ?? String(fault.code);
  const severity = Severity[fault.severity] ?? String(fault.severity);

  const clearableBy =
    ClearableBy[fault.clearableBy] ?? String(fault.clearableBy);

  return `${fault.message} (code=${code} severity=${severity} clearableBy=${clearableBy})`;
}

const ns = log.namespace('ExternalControlStep');

type Arguments = zod.infer<typeof Arguments>;
type Variables = zod.infer<typeof Variables>;

export default class ExternalControlStep extends Step<Arguments, Variables> {
  public static areSubstepsRequired = true;

  public static Arguments = Arguments;

  public static Variables = Variables;

  public substeps: Array<Step<object, object>> = [];

  private controlSource: string | null = null;

  private streamStarted: boolean = false;

  private clientId: string = `external-control-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

  /** Live TC servo control-plane session, when streaming is TC-routed. */
  private tcServoSession: TcServoSession | null = null;

  /**
   * The typed fault the session reported, if any. Held rather than flattened to
   * a string so the failure message can carry the code, severity and who may
   * clear it — the three things a client branches on.
   */
  private tcServoFault: Fault | null = null;

  /** True once a session-ending path (TC fault, or a pause — see _pause)
   * already called fail() for this play cycle, so the other paths and the
   * post-substep backstop don't fail the step a second time. */
  private sessionFailIssued = false;

  /** Set on the first release request; blocks a TC servo session from starting
   * after teardown has already begun. */
  private releaseRequested = false;

  /** Single shared teardown: _play's finally, _pause, and _stop can all request
   * a release concurrently — they must share one run so the lease is released
   * exactly once, and only after the TC teardown reached IDLE. */
  private releaseControlSourcePromise: Promise<void> | null = null;

  public initializeVariableState(): void {
    // Surface the session modality and its effective ServoJ tuning in the
    // runner state so any client can read the live session directly (see
    // Variables.ts). Resolved through the same helper the TC enqueue uses,
    // so what clients display is what the controller runs.
    this.variables = {
      controlledBy: this.args.controlledBy ?? 'api',
      streamType: this.args.streamType ?? 'pose',
      servoConfig: resolveServoConfig(this.args.servoConfig),
    };
  }

  public async _play({ playSubSteps, fail }: StepPlayArguments): Promise<void> {
    // Step instances are replayed (a Loop substep, or pause/resume), so each
    // play cycle must start with clean teardown/fault state. The teardown
    // memoization below is per-cycle by design; if it leaked into the next
    // cycle, releaseControlSource() would no-op and leak the re-acquired
    // lease/stream, and the stale releaseRequested/fault flags would fail every
    // later play. (streamStarted/controlSource already self-reset inside
    // endExternalStream()/releaseControlSource().)
    this.releaseRequested = false;
    this.releaseControlSourcePromise = null;
    this.tcServoFault = null;
    this.sessionFailIssued = false;

    if (this.routineContext.robot.robotKind !== 'live') {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'ExternalControl',
        },
        failureReason: 'External Control Step not supported in visualizer',
      });
    }

    // Require feature flag
    if (!getFeatureFlagSync('externalControlStep', false)) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'ExternalControl',
        },
        failureReason:
          'External Control Step requires the externalControlStep feature flag to be enabled',
      });
    }

    // Require CSM feature flag
    if (!getFeatureFlagSync('useControlSourceManager', false)) {
      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'ExternalControl',
        },
        failureReason:
          'External Control Step requires the Control Source Manager feature flag to be enabled',
      });
    }

    try {
      // Resolved once per play cycle and threaded through: it decides both the
      // lease and whether a primitive is enqueued, and its fallback warnings
      // would otherwise be emitted twice for a single session.
      const tcServoModality = this.tcServoModality();

      // Get control source (throws if incompatible)
      const controlSource = this.getControlSource(tcServoModality);
      // Request control source
      await this.requestControlSource(controlSource);

      // For a TC-routed servo session, enqueue the servo primitive on the
      // trajectory controller BEFORE starting the external stream, so ECB only
      // begins pushing targets once the primitive is RUNNING. The lease was
      // acquired just above — the primitive seeds its filter from the commanded
      // position / pose, so the lease must be held before the enqueue.
      if (tcServoModality) {
        await this.startTcServoSession(tcServoModality, fail);
      }

      // A stop/pause can land while startTcServoSession() awaits the
      // ClearError+Enqueue window — which resolves normally (not via throw), so
      // control falls through here. Teardown has already run the memoized
      // release and dropped the lease; starting the stream now would leak a ROS
      // bridge with no lease backing it. Bail before startExternalStream.
      if (this.releaseRequested) {
        return;
      }

      // Begin the external-control session: starts the ROS bridge for
      // rosHumble/rosJazzy; a no-op for api (the client speaks the SDK directly).
      await this.startExternalStream(tcServoModality);

      // Play substeps if any
      if (this.substeps && this.substeps.length > 0) {
        await playSubSteps();
      }

      // Backstop for a fault that lands exactly while the substeps unwind —
      // the live-session path fails the step from onFault itself (nothing
      // interrupts the substeps, so waiting until here would never run).
      // Recovery is a client-driven restart, never an auto re-arm.
      if (this.tcServoFault && !this.sessionFailIssued) {
        throw new Error(describeFault(this.tcServoFault));
      }
    } catch (error) {
      log.error(ns`play`, 'Error in external control step', { error });

      return fail({
        failure: {
          kind: FailureKind.StepPlayFailure,
          stepKind: 'ExternalControl',
        },
        failureReason: `External Control Step failed: ${(error as Error)?.message}`,
        error: error as Error,
      });
    } finally {
      // Release control source on exit
      await this.releaseControlSource();
    }
  }

  public async _pause(args: StepAction): Promise<void> {
    // A paused session can never resume: pausing releases the lease and ends
    // the external stream (below), and resume has no re-acquisition path —
    // the lease is only taken on a fresh play cycle, and _resume is a no-op.
    // A "resumed" session would report RoutineRunning while every external
    // command is silently dropped against a lease nobody holds. So any pause
    // — an operator pause or a pausing failure (collision, safety watchdog,
    // control-system event) — ends the session: tear down, and fail the
    // routine so the robot reaches a recoverable terminal state and the
    // client re-establishes by starting a new session (mirroring the
    // TC-fault path in startTcServoSession).
    const alreadyEnding = this.releaseRequested || this.sessionFailIssued;

    const released = this.releaseControlSource();

    if (!alreadyEnding) {
      this.sessionFailIssued = true;

      // Fire the failure without waiting out the teardown: for as long as
      // the pause lasts the routine looks resumable, and a resume would
      // revive the substeps with no session behind them.
      Promise.resolve(
        args.fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'ExternalControl',
          },
          failureReason: `External control session ended: ${
            args.reason ?? 'the routine was paused'
          }. Start a new session to resume streaming.`,
        }),
      ).catch((error) => {
        log.error(ns`pause`, 'Failed to fail the routine on pause', {
          error,
          clientId: this.clientId,
        });
      });
    }

    await released;
  }

  public async _stop(): Promise<void> {
    // Release control source when stopped
    await this.releaseControlSource();
  }

  /**
   * Get the control source this step acquires from arm-control-bot, based on the
   * configured `streamType`. Each modality maps to the source arm-control-bot tags
   * the corresponding stream with, so the lease matches exactly and no command is
   * silently dropped:
   *
   *  - pose            → COMPLIANCE_JOINT_TRAJECTORY when complianceMode is on
   *                      (pose routes through the compliance/pose/write path),
   *                      otherwise ROS_POSE — the source arm-control-bot's
   *                      /hardware/pose/write subscriber tags commands with
   *                      (PoseSubscriber::DEFAULT_SOURCE), so the lease matches.
   *  - joints          → EXTERNAL_JOINTS: joint setpoints stream on the dedicated
   *                      `stream_joints` topic, kept off the trajectory controller's
   *                      /hardware/joint_trajectory so the two never collide.
   *  - jointTrajectory → ROS_JOINT_TRAJECTORY: the scaled_joint_trajectory_controller
   *                      runs unchanged; SbotHW feeds /hardware/joint_trajectory tagged
   *                      ROS_JOINT_TRAJECTORY.
   *
   * A TC-routed setpoint modality instead leases `TRAJECTORY_CONTROLLER`, the
   * TC's output tag whichever primitive is running. `tcServoModality` is passed
   * in rather than recomputed so the lease and the enqueue decision are the
   * same value; either half without the other is silently dropped commands.
   */
  private getControlSource(tcServoModality: TcServoModality | null): string {
    if (tcServoModality) {
      return 'TRAJECTORY_CONTROLLER';
    }

    const streamType = this.args.streamType ?? 'pose';

    switch (streamType) {
      case 'joints':
        return 'EXTERNAL_JOINTS';
      case 'jointTrajectory':
        return 'ROS_JOINT_TRAJECTORY';
      case 'pose':
      default:
        return getFeatureFlagSync('complianceMode', false)
          ? 'COMPLIANCE_JOINT_TRAJECTORY'
          : 'ROS_POSE';
    }
  }

  /**
   * The servo modality this session streams through the trajectory controller,
   * or null when streaming is not TC-routed.
   *
   * The rule itself is `resolveTcServoModality`, shared with the Streaming UI.
   * Read once per play cycle and threaded through, so the lease and the
   * primitive enqueue cannot drift apart — either half without the other is
   * silently dropped commands.
   */
  private tcServoModality(): TcServoModality | null {
    const streamType = this.args.streamType ?? 'pose';

    const flags: TcStreamingFlags = {
      onlinePlannerStreaming: getFeatureFlagSync(
        'onlinePlannerStreaming',
        false,
      ),
      useTrajectoryController: getFeatureFlagSync(
        'useTrajectoryController',
        false,
      ),
      zmqPoseMigration: getFeatureFlagSync('zmqPoseMigration', false),
      onlinePlannerAllowedOnModel: isOnlinePlannerAllowedForThisModel(),
    };

    const modality = resolveTcServoModality(streamType, flags);

    if (!modality) {
      // Falling back silently would lease the arm to the direct path while the
      // operator believes the online planner is driving it.
      const fallback = describeTcStreamingFallback(streamType, flags);

      if (fallback) {
        log.warn(ns`tcServoModality`, fallback);
      }

      return null;
    }

    // The TC path wins over complianceMode, matching external-control-bot's
    // branch order. Warn rather than silently pick between two mutually
    // exclusive pose paths.
    if (modality === 'pose' && getFeatureFlagSync('complianceMode', false)) {
      log.warn(
        ns`tcServoModality`,
        'complianceMode and onlinePlannerStreaming are both enabled for a pose stream; routing through the trajectory controller and ignoring compliance',
      );
    }

    return modality;
  }

  /**
   * Enqueue the servo primitive and begin observing the TC. Runs after the
   * lease is acquired and before the external stream starts. The high-rate
   * target stream itself is pushed by external-control-bot; the step owns only
   * the control-plane.
   */
  private async startTcServoSession(
    modality: TcServoModality,
    fail: StepPlayArguments['fail'],
  ): Promise<void> {
    // A stop/pause may already have run the (memoized) release while _play was
    // still between awaits — don't start a session nothing will tear down.
    if (this.releaseRequested) {
      throw new Error(
        'External control session ended before the TC servo session started',
      );
    }

    const connector = this.routineContext.trajectoryControllerConnector;

    if (!connector) {
      throw new Error(
        'onlinePlannerStreaming is enabled but no trajectoryControllerConnector is available',
      );
    }

    this.tcServoSession = new TcServoSession(connector, this.routineContext, {
      modality,
      // Snapshotted once here, so a tooltip change mid-session cannot move the
      // controlled point out from under a running stream. Identity when the
      // Active Tooltip is the wrist, leaving the flange as that point.
      flangeToTcp:
        modality === 'pose'
          ? this.routineContext.activeTooltipTransform()
          : undefined,
      // Customer-tunable servo settings from the start() request; omitted
      // fields fall back to the defaults inside TcServoSession.
      servoConfig: this.args.servoConfig,
      onFault: (fault) => {
        this.tcServoFault ??= fault;

        log.error(ns`tcServoFault`, 'TC servo session faulted', {
          code: fault.code,
          severity: fault.severity,
          clearableBy: fault.clearableBy,
          clientId: this.clientId,
        });

        // Nothing to report once teardown has begun, and reporting anyway
        // would strand the record.
        //
        // The session latch is cleared only by a `failureState -> null`
        // transition. On this path `fail()` is skipped (below), so the routine
        // finishes normally — and a normal finish writes
        // `failureState: this.state.failureState` rather than the literal
        // `null`, so no clearing transition ever runs. A fault authored here
        // would be republished at 5 Hz for the life of the process and would
        // outrank later live faults of the same severity. The condition is
        // also not worth reporting: the session is ending because the client
        // or the operator asked it to.
        if (this.releaseRequested) {
          return;
        }

        // Hand the typed fault to the record stream before failing the
        // routine. `fail` below cannot do it: it sees only
        // FailureKind.StepPlayFailure, which maps to no customer-facing code,
        // so without this the client learns nothing — not even for the codes
        // that do reach the stream by another route, because the ones that
        // ride PRIMITIVE_COMPLETE (CLIENT_STREAM_TIMEOUT) never arrive there
        // at all.
        //
        // Deliberately above the sessionFailIssued guard rather than beside
        // the releaseRequested one: that guard exists only to avoid failing
        // the routine twice, and by the time it is set `fail()` has already
        // run, so a clearing transition is guaranteed and re-authoring is safe.
        this.routineContext.authorExternalControlSessionFault(fault);

        // Fail the routine from here: during a live session _play is parked
        // inside the substeps and nothing else interrupts them, so a session
        // that dies (stream timeout, TC fault) would otherwise leave the
        // routine "running" with the lease held while the data plane is dead.
        // Failing stops the running steps, which drives the ordered teardown
        // (_stop → releaseControlSource). Never auto-recover — the client
        // re-establishes by starting a new session.
        if (this.sessionFailIssued) {
          return;
        }

        this.sessionFailIssued = true;

        fail({
          failure: {
            kind: FailureKind.StepPlayFailure,
            stepKind: 'ExternalControl',
          },
          failureReason: `External Control Step failed: ${describeFault(fault)}`,
        }).catch((error) => {
          log.error(ns`tcServoFault`, 'Failed to fail the routine', { error });
        });
      },
    });

    await this.tcServoSession.start();
  }

  /**
   * Stop the TC servo primitive (Stop → wait IDLE → ClearQueue) if one is
   * running. Idempotent; safe on every teardown path.
   */
  private async stopTcServoSession(): Promise<void> {
    const session = this.tcServoSession;

    if (!session) {
      return;
    }

    // stop() is idempotent and waits out an in-flight start(), so a session
    // that was still enqueueing is stopped here rather than orphaned. Only
    // clear the field once teardown actually finished.
    await session.stop();
    this.tcServoSession = null;
  }

  /**
   * Request control source
   */
  private async requestControlSource(source: string): Promise<void> {
    this.controlSource = source;

    log.info(ns`requestControlSource`, 'Requesting control source', {
      source,
      clientId: this.clientId,
    });

    const result =
      await this.routineContext.armControlBotConnector?.SetControlSource({
        value: source,
        clientId: this.clientId,
      });

    if (result?.error) {
      throw new Error(
        `Failed to acquire control source: ${result.error.message}`,
      );
    }
  }

  /**
   * Release control source using the same logic as teleop step
   * Matches stopAllBotsInternal() from libs/sb-robot-api/src/controllers/teleop/teleop.ts
   *
   * Reentrant-safe: concurrent callers share one teardown run.
   */
  private releaseControlSource(): Promise<void> {
    this.releaseRequested = true;
    this.releaseControlSourcePromise ??= this.doReleaseControlSource();

    return this.releaseControlSourcePromise;
  }

  private async doReleaseControlSource(): Promise<void> {
    // Each stage is isolated: this promise is memoized and shared by _pause,
    // _stop, and _play's finally, so a rejection would (a) skip the remaining
    // stages — leaving the servo primitive running and the lease held with
    // the external client still able to drive the arm — and (b) never be
    // retried, since the memoization only resets on the next play cycle. The
    // pause path also invokes step.pause() without awaiting it, so a rejection
    // here would surface as an unhandled rejection and take botman down.

    // End the external-control session first (stops the ROS bridge, if any).
    try {
      await this.endExternalStream();
    } catch (error) {
      log.error(
        ns`releaseControlSource`,
        'Failed to end the external stream; continuing teardown',
        { error, clientId: this.clientId },
      );
    }

    // Then decelerate the TC servo primitive to IDLE and drop the queue BEFORE
    // releasing the lease — releasing mid-STOPPING forces a spurious TC error.
    try {
      await this.stopTcServoSession();
    } catch (error) {
      log.error(
        ns`releaseControlSource`,
        'Failed to stop the TC servo session; continuing teardown',
        { error, clientId: this.clientId },
      );
    }

    if (!this.controlSource) {
      return;
    }

    const source = this.controlSource;

    log.info(ns`releaseControlSource`, 'Releasing control source', {
      source,
      clientId: this.clientId,
    });

    try {
      await this.routineContext.armControlBotConnector?.ReleaseControlFromSource(
        {
          value: source,
          clientId: this.clientId,
        },
      );

      this.controlSource = null;
    } catch (error) {
      log.error(ns`releaseControlSource`, 'Failed to release control source', {
        error,
        source,
        clientId: this.clientId,
      });
    }
  }

  /** Map the user-facing `controlledBy` arg to the messenger ControlType. */
  private controlType(): ControlType {
    switch (this.args.controlledBy ?? 'api') {
      case 'rosHumble':
        return ControlType.CONTROL_TYPE_ROS_HUMBLE;
      case 'rosJazzy':
        return ControlType.CONTROL_TYPE_ROS_JAZZY;
      case 'api':
      default:
        return ControlType.CONTROL_TYPE_API;
    }
  }

  /** Map the user-facing `streamType` arg to the messenger StreamType (logged
   * only by external-control-bot — the lease is what actually gates commands). */
  private protoStreamType(): StreamType {
    switch (this.args.streamType ?? 'pose') {
      case 'joints':
        return StreamType.STREAM_TYPE_JOINTS;
      case 'jointTrajectory':
        return StreamType.STREAM_TYPE_TRAJECTORY;
      case 'pose':
      default:
        return StreamType.STREAM_TYPE_POSE;
    }
  }

  /**
   * Tell external-control-bot to begin the session. For rosHumble/rosJazzy this
   * spins up the ros-distro-bridge with the matching distro; for api it is a
   * no-op (the client speaks the SDK protocol directly).
   */
  private async startExternalStream(
    tcServoModality: TcServoModality | null,
  ): Promise<void> {
    // Set the flag before the call so teardown always ends the session even if
    // the request races a failure after the bridge has started.
    this.streamStarted = true;

    log.info(ns`startExternalStream`, 'Starting external stream', {
      controlledBy: this.args.controlledBy ?? 'api',
      streamType: this.args.streamType ?? 'pose',
      tcServoModality,
      clientId: this.clientId,
    });

    await this.routineContext.externalControlBotConnector?.StartExternalStream({
      controlType: this.controlType(),
      streamType: this.protoStreamType(),
      // Told, not re-derived: external-control-bot cannot see the flags this
      // decision needs, and a second opinion there would push setpoints to a
      // sink whose primitive was never enqueued.
      routeThroughTrajectoryController: tcServoModality !== null,
    });
  }

  /** End the external-control session (stops the ROS bridge if one was started). */
  private async endExternalStream(): Promise<void> {
    if (!this.streamStarted) {
      return;
    }

    this.streamStarted = false;

    log.info(ns`endExternalStream`, 'Ending external stream', {
      clientId: this.clientId,
    });

    await this.routineContext.externalControlBotConnector?.EndExternalStream(
      {},
    );
  }
}
